import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { confirmDialog, cancelDialog } from "../../state/slices/confirmationDialogSlice";

/**
 * Parses validation messages that may contain multiple row errors
 * Format: "Row 1: message Row 2: message" or just "message"
 */
type ValidationMessageItem = {
  row?: number;
  text: string;
  isWarning?: boolean;
  isHeader?: boolean;
};

function parseValidationMessages(message: string): ValidationMessageItem[] {
  const results: ValidationMessageItem[] = [];
  const lines = message
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const bulletLines = lines.filter(
    (line) => line.startsWith("•") || line.startsWith("-")
  );

  if (bulletLines.length > 0) {
    const headerLines = lines.filter(
      (line) => !line.startsWith("•") && !line.startsWith("-")
    );
    if (headerLines.length > 0) {
      results.push({ text: headerLines.join(" "), isHeader: true });
    }
    bulletLines.forEach((line) => {
      results.push({
        text: line.replace(/^[•-]\s*/, "").trim(),
      });
    });
    return results;
  }

  // Split by "Row X:" pattern
  const rowPattern = /Row\s+(\d+):\s*(.+?)(?=Row\s+\d+:|$)/gi;
  const matches = [...message.matchAll(rowPattern)];

  if (matches.length > 0) {
    matches.forEach((match) => {
      const rowNum = parseInt(match[1], 10);
      const text = match[2].trim();
      const isWarning =
        text.toLowerCase().includes("out of stock") ||
        text.toLowerCase().includes("should not be greater");
      results.push({ row: rowNum, text, isWarning });
    });
  } else {
    const isWarning =
      message.toLowerCase().includes("out of stock") ||
      message.toLowerCase().includes("should not be greater");
    results.push({ text: message.trim(), isWarning });
  }

  return results;
}

export const ConfirmationDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, message, title, requireReason, reasonLabel } = useAppSelector(
    (state) => state.confirmationDialog
  );

  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const okButtonRef = useRef<HTMLButtonElement>(null);
  const [reasonText, setReasonText] = useState("");
  const [reasonError, setReasonError] = useState("");
  const showReasonField = Boolean(requireReason || reasonLabel);

  // Handle keyboard navigation (Escape, Arrow keys, Enter)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key - cancel dialog
      if (e.key === "Escape") {
        e.preventDefault();
        dispatch(cancelDialog());
        return;
      }

      // Arrow Left - focus Cancel button
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        cancelButtonRef.current?.focus();
        return;
      }

      // Arrow Right - focus OK button
      if (e.key === "ArrowRight") {
        e.preventDefault();
        okButtonRef.current?.focus();
        return;
      }

      // Enter key - trigger focused button
      if (e.key === "Enter") {
        const activeElement = document.activeElement;
        if (activeElement === cancelButtonRef.current) {
          e.preventDefault();
          dispatch(cancelDialog());
        } else if (activeElement === okButtonRef.current) {
          e.preventDefault();
          dispatch(confirmDialog());
        }
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen) {
      setReasonText("");
      setReasonError("");
      setTimeout(() => {
        if (showReasonField) {
          const el = document.getElementById("confirm-dialog-reason-input");
          (el as HTMLTextAreaElement | null)?.focus();
        } else {
          okButtonRef.current?.focus();
        }
      }, 0);
    }
  }, [isOpen, showReasonField]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const trimmed = reasonText.trim();
    if (requireReason && !trimmed) {
      setReasonError(
        `${reasonLabel || "Reason for job card approval"} is required.`
      );
      return;
    }
    dispatch(confirmDialog({ reason: trimmed }));
  };

  const handleCancel = () => {
    dispatch(cancelDialog());
  };

  // Use a proper title instead of hostname
  const dialogTitle = title || "Confirm Action";
  const isValidationDialog =
    title === "Validation" ||
    title === "Validation Error" ||
    title === "Validation Warning" ||
    title === "Purchase Return Not Allowed" ||
    title === "Purchased Items on Job Card";
  const parsedMessages = isValidationDialog ? parseValidationMessages(message) : null;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 z-[9998] transition-colors duration-200 ${
          isValidationDialog 
            ? "bg-black/30 dark:bg-black/50 backdrop-blur-sm" 
            : "bg-black/20 dark:bg-black/40"
        }`}
        onClick={isValidationDialog ? handleConfirm : handleCancel}
        style={isValidationDialog ? {} : { backdropFilter: "blur(0px)" }}
      />

      {/* Dialog */}
      <div
        className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[9999] bg-white dark:bg-gray-800 shadow-2xl transition-all duration-200 ${
          isValidationDialog ? "min-w-[400px] max-w-[600px]" : "min-w-[300px] max-w-[500px]"
        }`}
        style={{
          fontFamily: "system-ui, -apple-system, sans-serif",
          borderRadius: isValidationDialog ? "12px" : "4px",
          maxHeight: isValidationDialog ? "80vh" : "auto",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
      >
        {/* Title bar */}
        {isValidationDialog ? (
          <div
            className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 transition-colors duration-200"
            id="dialog-title"
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {dialogTitle}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                  {title === "Purchase Return Not Allowed"
                    ? "Complete Parts Issue Return before continuing"
                    : title === "Purchased Items on Job Card"
                      ? "Review purchased lines before requesting or issuing"
                      : "Please review the following issues"}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 transition-colors duration-200"
            style={{
              fontSize: "13px",
              fontWeight: 600,
              userSelect: "none",
            }}
            id="dialog-title"
          >
            <span className="text-gray-900 dark:text-gray-100">{dialogTitle}</span>
          </div>
        )}

        {/* Message content */}
        {isValidationDialog && parsedMessages && parsedMessages.length > 0 ? (
          <div
            className="px-6 py-4 overflow-y-auto flex-1"
            style={{ maxHeight: "calc(80vh - 140px)" }}
          >
            <div className="space-y-3">
              {parsedMessages.map((item, index) => {
                const isWarning = item.isWarning;
                if (item.isHeader) {
                  return (
                    <p
                      key={index}
                      className="text-sm leading-relaxed text-gray-700 dark:text-gray-200"
                    >
                      {item.text}
                    </p>
                  );
                }
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-l-4 ${
                      isWarning
                        ? "bg-amber-50 dark:bg-amber-900/20 border-amber-400 dark:border-amber-500"
                        : "bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-500"
                    } transition-colors duration-200`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {isWarning ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-amber-600 dark:text-amber-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-red-600 dark:text-red-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {item.row && (
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                isWarning
                                  ? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
                                  : "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200"
                              }`}
                            >
                              Row {item.row}
                            </span>
                            {isWarning && (
                              <span className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                                Warning
                              </span>
                            )}
                          </div>
                        )}
                        <p
                          className={`text-sm leading-relaxed ${
                            isWarning
                              ? "text-amber-900 dark:text-amber-100"
                              : "text-red-900 dark:text-red-100"
                          }`}
                        >
                          {item.text}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div
            className={`px-4 py-4 text-gray-900 dark:text-gray-100 transition-colors duration-200 ${isValidationDialog ? "px-6 overflow-y-auto flex-1" : ""}`}
            style={{ fontSize: "14px", maxHeight: isValidationDialog ? "calc(80vh - 140px)" : "auto" }}
          >
            <div>{message}</div>
            {showReasonField ? (
              <div className="mt-4">
                <label
                  htmlFor="confirm-dialog-reason-input"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                >
                  {reasonLabel || "Reason for job card approval"}
                  {requireReason ? (
                    <span className="text-red-600 dark:text-red-400"> *</span>
                  ) : null}
                </label>
                <textarea
                  id="confirm-dialog-reason-input"
                  rows={3}
                  value={reasonText}
                  onChange={(e) => {
                    setReasonText(e.target.value);
                    if (reasonError) setReasonError("");
                  }}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={
                    requireReason
                      ? reasonLabel?.toLowerCase().includes("void")
                        ? "Enter void reason"
                        : reasonLabel?.toLowerCase().includes("request")
                          ? "Enter reason for authorization request"
                          : "Enter reason for approval"
                      : "Enter request reason (optional)"
                  }
                />
                {reasonError ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{reasonError}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        {/* Buttons */}
        <div className={`px-4 py-3 flex justify-end gap-2 ${isValidationDialog ? "px-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 gap-3" : ""}`}>
          {!isValidationDialog && (
            <button
              ref={cancelButtonRef}
              onClick={handleCancel}
              className="px-4 py-1.5 text-sm font-medium border rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 transition-colors duration-200"
              style={{
                minWidth: "75px",
              }}
            >
              Cancel
            </button>
          )}
          <button
            ref={okButtonRef}
            onClick={handleConfirm}
            className={`text-sm font-medium rounded text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 transition-all duration-200 ${
              isValidationDialog
                ? "px-6 py-2.5 font-semibold rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                : "px-4 py-1.5"
            }`}
            style={{
              backgroundColor: "var(--color-primary-500, #35ba5b)",
              minWidth: isValidationDialog ? "100px" : "75px",
            }}
          >
            {isValidationDialog ? "Got it" : "OK"}
          </button>
        </div>
      </div>
    </>
  );
};
